// Phase 6.2 — scope selector ("Viewing: <user>").
//
// A superadmin-only dropdown that sits below the top nav on /browse
// and /status. Picking a user impersonates them: the page refetches
// and shows exactly what that user would see. Picking "All users"
// clears impersonation and restores the merged view.
//
// The dropdown IS the banner — when impersonating, the trigger
// shows the username with an accent tint + an eye icon, so the
// admin always knows whose data they're looking at. No separate
// banner element.
//
// Renders nothing for non-superadmins (gated on role) — they can
// only ever see their own data, so a selector would be redundant.

import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Eye, Users } from "lucide-react";

import { useAuth } from "../state/AuthContext";
import { useImpersonation } from "../state/ImpersonationContext";

export function ScopeSelector() {
  const { role, userId } = useAuth();
  const { current, setImpersonation, directory, directoryLoading } =
    useImpersonation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click + Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Gate: superadmin only. Regular users never see the selector.
  if (role !== "superadmin") return null;

  const impersonating = current !== null;
  const triggerLabel = impersonating ? current!.username : "All users";

  // Pass 6.3.5: superadmins are administrative roles, not data
  // owners — selecting one only ever yields an empty scope. Filter
  // them out so the dropdown lists "All users" + regular users only.
  const selectableUsers = directory.filter(({ user }) => user.role === "user");

  return (
    <div
      data-testid="scope-selector"
      data-impersonating={impersonating ? "true" : "false"}
      className="flex-shrink-0 inline-flex items-center"
    >
      <div className="relative" ref={rootRef}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="scope-selector-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          className={[
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-medium transition-colors duration-150",
            impersonating
              ? "border-theme-accent-link/50 text-theme-accent-link bg-theme-accent-bg/15"
              : "border-white/[0.1] bg-white/[0.04] text-theme-text-secondary hover:text-theme-text-primary hover:border-white/[0.2]",
          ].join(" ")}
        >
          {impersonating ? (
            <Eye className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
          ) : (
            <Users className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
          )}
          <span data-testid="scope-selector-current">{triggerLabel}</span>
          <ChevronDown className="w-3 h-3" strokeWidth={1.5} aria-hidden />
        </button>

        {open && (
          <div
            data-testid="scope-selector-menu"
            role="listbox"
            className="absolute right-0 mt-1.5 z-30 w-80 max-h-96 overflow-y-auto rounded-xl bg-theme-bg-card border border-white/[0.12] shadow-2xl"
          >
            {/* "All users" — default, distinct, first. */}
            <button
              type="button"
              role="option"
              aria-selected={!impersonating}
              data-testid="scope-option-all"
              onClick={() => {
                setImpersonation(null);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 flex items-center gap-2 hover:bg-theme-bg-conv-hover transition-colors duration-150"
            >
              <span className="w-4 flex-shrink-0">
                {!impersonating && (
                  <Check className="w-3.5 h-3.5 text-theme-accent-link" strokeWidth={2} aria-hidden />
                )}
              </span>
              <Users className="w-3.5 h-3.5 text-theme-text-tertiary flex-shrink-0" strokeWidth={1.5} aria-hidden />
              <span className="font-sans text-sm text-theme-text-primary">All users</span>
              <span className="font-mono text-[10px] text-theme-text-tertiary">(merged)</span>
            </button>

            <div className="border-t border-theme-border-default" />

            {directoryLoading && directory.length === 0 ? (
              <div
                data-testid="scope-options-loading"
                className="px-4 py-3 font-mono text-[11px] text-theme-text-tertiary"
              >
                Loading users…
              </div>
            ) : selectableUsers.length === 0 ? (
              <div className="px-4 py-3 font-mono text-[11px] text-theme-text-tertiary">
                No other users.
              </div>
            ) : (
              selectableUsers.map(({ user, workspaceCount }) => {
                const selected = current?.userId === user.user_id;
                const isSelf = user.user_id === userId;
                const initial = (user.username[0] ?? "?").toUpperCase();
                return (
                  <button
                    key={user.user_id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    data-testid={`scope-option-${user.user_id}`}
                    onClick={() => {
                      setImpersonation({
                        userId: user.user_id,
                        username: user.username,
                        email: user.email,
                      });
                      setOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 flex items-center gap-2 hover:bg-theme-bg-conv-hover transition-colors duration-150"
                  >
                    <span className="w-4 flex-shrink-0">
                      {selected && (
                        <Check className="w-3.5 h-3.5 text-theme-accent-link" strokeWidth={2} aria-hidden />
                      )}
                    </span>
                    <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-theme-bg-chip-subtle font-mono text-[11px] text-theme-text-secondary">
                      {initial}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-sans text-sm text-theme-text-primary truncate">
                        {user.username}
                        {isSelf && (
                          <span className="ml-1 font-mono text-[10px] text-theme-text-tertiary">
                            (you)
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-[10px] text-theme-text-tertiary truncate">
                        {user.email ?? "—"} ·{" "}
                        {workspaceCount} workspace{workspaceCount === 1 ? "" : "s"}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
